<template>
  <v-card>
    <v-card-title class="bg-blue-grey-lighten-5 text-primary">
      欠席連絡
    </v-card-title>

    <v-form ref="post_absence_form">
      <v-card-text class="py-3">
        
        <v-dialog
          v-model="isDatePicker"
          width="auto"
          scroll-strategy="block"
        >
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="ios-click-barrier">
              <v-text-field
                v-model="dateRangeText"
                label="期間"
                prepend-icon="mdi-calendar"
                :rules="[rules.required]"
                variant="outlined"
                density="compact"
                readonly
                inputmode="none"
                tabindex="-1"
                class="pointer-events-none"
              ></v-text-field>
            </div>
          </template>

          <v-date-picker
            v-model="dates"
            locale="ja"
            :min="allowMinDate"
            title=""
            multiple="range"
            view-mode="month" 
            elevation="12"
            class="rounded-lg"
            @update:model-value="handleDateUpdate"
            color="primary"
          >
            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" color="primary" @click="isDatePicker = false">
                OK
              </v-btn>
            </template>
          </v-date-picker>
        </v-dialog>

        <div class="mt-4"></div>

        <div style="position: relative; z-index: 20;">
          <v-select
            v-model="selectedReason"
            :items="reasonOptions"
            item-title="title"
            item-value="value"
            :rules="[rules.required]"
            label="理由"
            variant="outlined"
            density="compact"
            :menu-props="{ contentClass: 'bg-white elevation-8' }"

          ></v-select>
        </div>

        <div class="mt-2"></div>

        <div style="position: relative; z-index: 10;">
          <v-select
            v-if="selectedReason === 'infection'"
            v-model="infection"
            :items="infectionOptions"
            item-title="title"
            item-value="value"
            :rules="[rules.required]"
            label="詳細"
            variant="outlined"
            class="infection-select"
            :menu-props="{ 
              contentClass: 'bg-white elevation-8 infection-select-menu'
            }"
            density="compact"
          ></v-select>
        </div>

        <div class="mt-2"></div>

        <v-textarea
          v-if="selectedReason === 'others'"
          v-model="comment"
          :rules="[rules.required, rules.counter500]"
          label="コメント"
          variant="outlined"
          density="compact" 
        />
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close" color="error" variant="text"> キャンセル </v-btn>
        <div style="width: 16px;"></div>
        <v-btn 
          @click="clickPost" 
          :loading="btnLoading" 
          color="primary" 
          variant="flat"
        >
          送信
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import rules from '@/service/ValidRules';
import { chatModule } from '@/store/entityModules/chat';

interface DataType {
  dates: any;
  startedAt: string;
  endedAt: string;
  reason: string;
  comment: string | null;
  selectedReason: string;
  infection: string;
  isDatePicker: boolean;
  btnLoading: boolean;
}

export default defineComponent({
  name: 'AbsenceModal',
  props: {
    isOpen: { type: Boolean, default: false },
    childId: { type: Number, default: NaN },
  },
  emits: ['close', 'posted'],
  
  data: (): DataType => ({
    dates: [],
    startedAt: '',
    endedAt: '',
    reason: '',
    comment: '',
    selectedReason: '',
    infection: '',
    isDatePicker: false,
    btnLoading: false,
  }),

  computed: {
    rules() { return rules; },
    form(): any { return this.$refs.post_absence_form; },
    allowMinDate(): any {
      return new Date();
    },
    dateRangeText(): string {
      if (!this.dates || (Array.isArray(this.dates) && this.dates.length === 0)) {
        return '';
      }

      const dateArray = Array.isArray(this.dates) ? this.dates : [this.dates];
      const sorted = [...dateArray]
        .filter(d => d)
        .sort((a, b) => dayjs(a).unix() - dayjs(b).unix());

      if (sorted.length === 1) {
        return dayjs(sorted[0]).format('YYYY/MM/DD');
      }
      return `${dayjs(sorted[0]).format('YYYY/MM/DD')} ~ ${dayjs(sorted[sorted.length - 1]).format('YYYY/MM/DD')}`;
    },
    reasonOptions() {
      return [
        { title: '発熱', value: 'fever' },
        { title: '家庭の事情', value: 'personal' },
        { title: '感染症', value: 'infection' },
        { title: 'その他（コメントの入力が必要）', value: 'others' },
      ];
    },
    infectionOptions() {
      return [
        { title: '溶連菌感染症', value: 'infStrep' },
        { title: 'マイコプラズマ肺炎', value: 'infMycoplasma' },
        { title: '手足口病', value: 'infHFMD' },
        { title: '伝染性紅斑（りんご病）', value: 'infSlappedCheek' },
        { title: 'ウイルス性胃腸炎（ノロ、ロタ、アデノ等）', value: 'infStomach' },
        { title: 'ヘルパンギーナ', value: 'infHerpangina' },
        { title: 'RSウィルス感染症', value: 'infRS' },
        { title: '帯状疱疹', value: 'infZoster' },
        { title: '突発性発しん', value: 'infRoseola' },
        { title: '伝染性膿痂疹（とびひ）', value: 'infImpetigo' },
        { title: '新型コロナウィルス', value: 'covid19' },
        { title: 'インフルエンザA型', value: 'infInfluenzaA' },
        { title: 'インフルエンザB型', value: 'infInfluenzaB' },
      ];
    },
  },

  watch: {
    isOpen: {
      handler(val: boolean) {
        if (val) {
          this.dates = [];
          this.selectedReason = '';
          this.infection = '';
          this.comment = '';
        }
      },
      immediate: true,
    },
  },

  methods: {
    handleDateUpdate(val: any) {
      // 必要な処理があれば記述
    },
    close() {
      this.$emit('close');
    },
    async clickPost() {
      const { valid } = await this.form.validate();
      if (!valid) return;

      const dateArray = Array.isArray(this.dates) ? this.dates : [this.dates];
      if (dateArray.length === 0) return;

      const sorted = [...dateArray].sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
      this.startedAt = dayjs(sorted[0]).format('YYYY-MM-DD');
      this.endedAt = dayjs(sorted[sorted.length - 1]).format('YYYY-MM-DD');

      if (this.selectedReason === 'infection') {
        this.reason = this.infection;
        this.comment = null;
      } else if (this.selectedReason === 'others') {
        this.reason = 'others';
      } else {
        this.reason = this.selectedReason;
        this.comment = null;
      }

      this.btnLoading = true;
      try {
        await chatModule.postHomeAbsence({
          childId: this.childId,
          startedAt: this.startedAt,
          endedAt: this.endedAt,
          reason: this.reason,
          comment: this.comment,
        });
        this.$emit('posted');
        this.$emit('close');
      } catch (error) {
        console.error(error);
        this.$emit('close');
      } finally {
        this.btnLoading = false;
      }
    },
  },
});
</script>

<style>
/* iOS向けの透明なバリアCSS */
.ios-click-barrier {
  cursor: pointer;
  display: block;
  width: 100%;
}
.pointer-events-none {
  pointer-events: none !important;
}

/* 感染症セレクトボックスのデザイン調整 */
.infection-select-menu .v-list-item {
  min-height: 48px;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-inline: 12px !important; 
}
.infection-select-menu .v-list-item-title {
  font-size: 14px !important; 
  white-space: normal !important; /* 長い病名が折り返されるように設定 */
}
</style>

<style scoped>
.infection-select :deep(.v-field__input) {
  font-size: 14px !important;
}

/* ⭕️ iPhone SE などの極端に幅が狭い端末でもカレンダーを強制的に収めるための安全策 */
@media (max-width: 360px) {
  :deep(.v-date-picker) {
    transform: scale(0.9);
    transform-origin: center center;
  }
}
</style>